import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wrench, X } from 'lucide-react';
import {
  Field,
  TextInput,
  SelectInput,
  TextareaInput,
  Switch,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../ui/Field';
import type { Tool, ToolModel } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// ToolForm — Operations Deck modal for Add/Edit tool (Inventory).
//
// Mirrors CompanyForm / SiteForm / UserForm shell:
//   • Sticky header — icon tile, breadcrumb caption, Fraunces title,
//     close button, Active/Inactive switch
//   • Error summary banner when submitted with errors
//   • Scrollable body — two columns:
//       LEFT  — 01 · Identity (model number + serial + mfg date) and
//               02 · Notes (note text)
//       RIGHT — Tool history info panel (last site, drilling hours,
//               circulating hours, below-rotary hours)
//   • Sticky footer — last-modified caption + Cancel / Save
//
// All inputs use the shared Field primitives so typography is
// identical to the Companies / Locations / Users forms.
// ─────────────────────────────────────────────────────────────

const emptyTool: Partial<Tool> = {
  toolModelId: 0,
  serialNumber: '',
  manufacturerDate: '',
  active: true,
  note: '',
  isUsed: false,
  lastKnownIPAddress: '',
  lastSiteLocation: '',
  totalHoursDrilling: 0,
  totalHoursLogging: 0,
  totalHoursCirculating: 0,
  totalHoursBelowRotary: 0,
  companyId: null,
  siteId: null,
};

export interface ToolErrors {
  toolModelId?: string;
  serialNumber?: string;
}

function validate(form: Partial<Tool>): ToolErrors {
  const e: ToolErrors = {};
  if (!form.toolModelId) e.toolModelId = 'Please select a tool model';
  if (!form.serialNumber?.trim()) e.serialNumber = 'Serial number is required';
  return e;
}

export interface ToolFormProps {
  open: boolean;
  editing: Tool | null;
  saving: boolean;
  toolModels: ToolModel[];
  onCancel: () => void;
  onSave: (form: Partial<Tool>) => Promise<void> | void;
}

export function ToolForm({
  open,
  editing,
  saving,
  toolModels,
  onCancel,
  onSave,
}: ToolFormProps) {
  const [form, setForm] = useState<Partial<Tool>>(emptyTool);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ToolErrors>({});

  // (Re)seed the form whenever the modal opens with a fresh editing target.
  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setErrors({});
    if (editing) {
      setForm({ ...editing });
    } else {
      setForm({ ...emptyTool, toolModelId: toolModels[0]?.id ?? 0 });
    }
  }, [open, editing, toolModels]);

  // Body scroll lock while modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape closes the modal (unless mid-save).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  if (!open) return null;

  const set = (key: keyof Tool | string, value: string | number | boolean | null) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const liveValidate = (next: Partial<Tool>) => {
    if (!submitted) return;
    setErrors(validate(next));
  };

  const handleSubmit = () => {
    const errs = validate(form);
    setErrors(errs);
    setSubmitted(true);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    void onSave(form);
  };

  const subtitle = editing ? `Inventory · Editing #${editing.id ?? ''}` : 'Inventory · New entry';
  const title = editing ? 'Edit tool' : 'Add tool to inventory';
  const errorCount = Object.keys(errors).length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tool-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 880,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <Wrench size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="tool-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 520,
                }}
              >
                Equipment registry entry. Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
            <button
              onClick={onCancel}
              aria-label="Close"
              disabled={saving}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-dim)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <X size={18} />
            </button>
            <Switch
              checked={!!form.active}
              onChange={(active) => set('active', active)}
              label={form.active ? 'Active' : 'Inactive'}
              description={form.active ? 'Available for assignment' : 'Hidden from active inventory'}
            />
          </div>
        </header>

        {/* ── Error summary ─────────────────────────────────── */}
        {submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>— please review the highlighted inputs.</span>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 280px',
            gap: 0,
            overflowY: 'auto',
            background: 'var(--color-panel)',
          }}
        >
          {/* LEFT — form */}
          <div
            style={{
              padding: '22px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 26,
            }}
          >
            {/* 01 Identity */}
            <FormSection number={1} title="Identity">
              <Field
                label="Model number"
                required
                error={submitted ? errors.toolModelId : undefined}
              >
                <div data-field="toolModelId">
                  <SelectInput
                    value={form.toolModelId ?? ''}
                    error={submitted && !!errors.toolModelId}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      set('toolModelId', v);
                      liveValidate({ ...form, toolModelId: v });
                    }}
                  >
                    <option value="">— Select tool model —</option>
                    {toolModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.toolModelName}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Serial number"
                  required
                  error={submitted ? errors.serialNumber : undefined}
                >
                  <div data-field="serialNumber">
                    <TextInput
                      value={form.serialNumber ?? ''}
                      placeholder="ACXUP475-001"
                      error={submitted && !!errors.serialNumber}
                      style={{ fontFamily: 'var(--font-mono)' }}
                      onChange={(e) => {
                        set('serialNumber', e.target.value);
                        liveValidate({ ...form, serialNumber: e.target.value });
                      }}
                    />
                  </div>
                </Field>
                <Field label="Manufacturer date">
                  <TextInput
                    type="date"
                    value={form.manufacturerDate?.split('T')[0] ?? ''}
                    style={{ fontFamily: 'var(--font-mono)' }}
                    onChange={(e) => set('manufacturerDate', e.target.value)}
                  />
                </Field>
              </div>
            </FormSection>

            {/* 02 Notes */}
            <FormSection
              number={2}
              title="Notes"
              description="Internal label or service notes — e.g. P003 — calibrated 2026-04-15"
            >
              <Field label="Note">
                <TextareaInput
                  value={form.note ?? ''}
                  placeholder="Anything the team should know about this tool…"
                  rows={4}
                  onChange={(e) => set('note', e.target.value)}
                />
              </Field>
            </FormSection>
          </div>

          {/* RIGHT — Tool history info panel */}
          <aside
            style={{
              background: 'var(--color-panel-alt)',
              borderLeft: '1px solid var(--color-border)',
              padding: '22px 22px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.4,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Tool history
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <InfoRow
                label="Last site location"
                value={form.lastSiteLocation || '—'}
                dim={!form.lastSiteLocation}
              />
              <InfoRow
                label="Total hours drilling"
                value={fmtHours(form.totalHoursDrilling)}
                mono
              />
              <InfoRow
                label="Total hours circulating"
                value={fmtHours(form.totalHoursCirculating)}
                mono
              />
              <InfoRow
                label="Total hours below rotary"
                value={fmtHours(form.totalHoursBelowRotary)}
                mono
                last
              />
            </div>

            <div
              style={{
                marginTop: 4,
                padding: 12,
                background: 'var(--color-panel)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 11.5,
                color: 'var(--color-text-mid)',
                lineHeight: 1.5,
              }}
            >
              Hours accumulate automatically as the tool is checked into wells. New tools start at zero.
            </div>
          </aside>
        </div>

        {/* ── Sticky footer ─────────────────────────────────── */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 0.6,
            }}
          >
            {editing
              ? `LAST UPDATE · ${formatTs(editing.lastModifiedDate ?? editing.createdDate)}`
              : 'NEW RECORD'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add tool'}
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components & helpers
// ─────────────────────────────────────────────────────────────

function InfoRow({
  label,
  value,
  mono = false,
  dim = false,
  last = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  dim?: boolean;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        borderTop: '1px solid var(--color-border)',
        borderBottom: last ? '1px solid var(--color-border)' : undefined,
      }}
    >
      <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-mid)' }}>{label}</div>
      <div
        style={{
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          fontSize: 12.5,
          color: dim ? 'var(--color-text-dim)' : 'var(--color-text)',
          fontWeight: 500,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function fmtHours(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '0.0';
  return Number(n).toFixed(1);
}

function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}
